import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, AlertTriangle, RefreshCcw, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ResetRequestFields, reasonOk, startVmcReset, useResetMode } from './VmcReset'

const ACTIONS = [
  {
    key: 'events',
    icon: RefreshCcw,
    titleKey: 'ops:devices.actions.events_title',
    descKey:  'ops:devices.actions.events_desc',
    danger: false,
    accent: 'text-yellow-600',
  },
  {
    key: 'device',
    icon: Trash2,
    titleKey: 'ops:devices.actions.device_title',
    descKey:  'ops:devices.actions.device_desc',
    danger: true,
    accent: 'text-red-600',
  },
]

export default function DeviceActionsModal({ device, onClose }) {
  const { t } = useTranslation()
  const [action, setAction] = useState('events')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [warnings, setWarnings] = useState(null)
  const [reason, setReason] = useState('')
  const [ticket, setTicket] = useState('')
  // Com o serviço de reset ligado, o remover pede motivo e o resultado é acompanhado
  // fora deste modal (VmcReset.jsx): quando o callback apaga o equipamento, o drawer some.
  const resetMode = useResetMode()

  const selected = ACTIONS.find(a => a.key === action)
  const viaService = selected.key === 'device' && resetMode === true
  // Mesma barra do expurgo coordenado no card do device pendente: o vmc-id digitado.
  // As duas telas disparam o MESMO DELETE (reset do VMC + IoT + broker + VPN + banco).
  const blocked = selected.danger && (
    confirm.trim() !== device.name || resetMode === null || (viaService && !reasonOk(reason))
  )

  // Esc fecha. Sem isto o modal ficava armado por baixo do drawer fechado e voltava
  // apontando para outro equipamento.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    if (viaService) {
      const msg = await startVmcReset(t, device.name, { reason, ticket })
      if (msg) {
        setError(msg)
        setSubmitting(false)
        return
      }
      onClose()
      return
    }
    try {
      const params = new URLSearchParams({ [action]: 'true' })
      const res = await fetch(`/api/devices/${encodeURIComponent(device.name)}?${params}`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const t = await res.text()
        throw new Error(t || `HTTP ${res.status}`)
      }
      const body = await res.json().catch(() => null)
      if (body?.warnings?.length) {
        setWarnings(body.warnings)
        return
      }
      onClose()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  // Portal para o body: o painel do Drawer tem translate-x (transform), que aprisiona
  // um fixed descendente — o scrim escurecia só o drawer e o resto da página seguia
  // clicável por trás do modal de remoção.
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">{t('ops:devices.actions.title')}</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">{device.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-2">
          {ACTIONS.map(opt => {
            const Icon = opt.icon
            const selected = action === opt.key
            return (
              <label
                key={opt.key}
                className={`flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors ${
                  selected ? 'border-slate-400 bg-slate-50 dark:bg-slate-900' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <input
                  type="radio"
                  name="action"
                  value={opt.key}
                  checked={selected}
                  onChange={() => { setAction(opt.key); setConfirm('') }}
                  className="mt-1 accent-slate-700"
                />
                <Icon size={16} className={`mt-0.5 shrink-0 ${opt.accent}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{t(opt.titleKey)}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {t(opt.key === 'device' && resetMode ? 'ops:devices.reset.intro' : opt.descKey)}
                  </div>
                </div>
              </label>
            )
          })}

          {selected.danger && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 rounded px-3 py-2 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />
                <div className="text-xs font-semibold text-red-700 dark:text-red-300">
                  {t('ops:devices.actions.confirm_removal')} <span className="font-mono">{device.name}</span>
                </div>
              </div>
              <label className="block text-[11px] text-red-700 dark:text-red-300">
                {t('debug:confirm.type_to_enable', { word: device.name })}
              </label>
              <input
                type="text"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder={device.name}
                spellCheck={false}
                autoComplete="off"
                className="w-full h-8 px-2 text-xs font-mono rounded border border-red-200 dark:border-red-500/30 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500/30"
              />
              {viaService && (
                <div className="pt-1">
                  <ResetRequestFields reason={reason} ticket={ticket} onReason={setReason} onTicket={setTicket} disabled={submitting} />
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}

          {warnings && (
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 rounded px-3 py-2">
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-800 dark:text-amber-300">
                {warnings.map(w => (
                  <p key={w}>{t(`ops:devices.actions.warn_${w}`, w)}</p>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {warnings ? (
              <button
                onClick={onClose}
                className="text-sm font-semibold px-3 py-1.5 rounded-md text-white bg-slate-800 hover:bg-slate-700"
              >
                {t('common:actions.close')}
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  onClick={submit}
                  disabled={submitting || blocked}
                  className={`text-sm font-semibold px-3 py-1.5 rounded-md text-white disabled:bg-slate-200 disabled:text-slate-400 ${
                    selected.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-800 hover:bg-slate-700'
                  }`}
                >
                  {submitting
                    ? (viaService ? t('ops:devices.reset.sending') : t('ops:devices.actions.applying'))
                    : (viaService ? t('ops:devices.reset.submit') : t('ops:devices.actions.apply'))}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
